import { EventBus } from '@/utils/event-bus'

const HEALTH_CHECK_INTERVAL = 30000 // 30s 健康检查间隔

class ApiHealthManager {
  constructor() {
    this.servers = []
    this.currentUrl = null
    this.statusMap = {} // { url: 'healthy' | 'unhealthy' }
    this.timer = null
  }

  /**
   * 初始化服务器列表
   * @param {string[]} serverUrls
   */
  init(serverUrls) {
    this.servers = [...serverUrls]
    this.servers.forEach(url => {
      this.statusMap[url] = 'healthy'
    })
  }

  /**
   * 设置当前服务器
   * @param {string} url
   */
  setCurrent(url) {
    this.currentUrl = url
  }

  /**
   * 获取当前服务器
   * @returns {string}
   */
  getCurrent() {
    return this.currentUrl
  }

  /**
   * 获取所有服务器列表
   * @returns {string[]}
   */
  getServers() {
    return [...this.servers]
  }

  /**
   * 切换到下一个健康的服务器（轮询）
   * 失败时由 axios 拦截器自动调用，或手动触发
   * @returns {string|null} 新的 URL，或 null（无可用服务器）
   */
  switchToNext() {
    if (this.servers.length <= 1) return null

    // 标记当前服务器为不健康
    this.statusMap[this.currentUrl] = 'unhealthy'
    const currentIndex = this.servers.indexOf(this.currentUrl)

    // 轮询查找下一个健康的服务器
    for (let i = 1; i <= this.servers.length; i++) {
      const idx = (currentIndex + i) % this.servers.length
      const url = this.servers[idx]
      // 如果该服务器健康，或者是最后一个可选项（兜底），则切换
      if (this.statusMap[url] !== 'unhealthy' || i === this.servers.length) {
        this.currentUrl = url
        window.baseURL = url
        EventBus.$emit('server-changed', url)
        return url
      }
    }
    return null
  }

  /**
   * Ping 单个服务器，检测是否可达
   * @param {string} url
   * @returns {Promise<boolean>}
   */
  async ping(url) {
    try {
      const ctrl = new AbortController()
      const timeout = setTimeout(() => ctrl.abort(), 5000)
      await fetch(url + '/img/1.png', { mode: 'no-cors', signal: ctrl.signal })
      clearTimeout(timeout)
      return true
    } catch {
      return false
    }
  }

  /**
   * 对所有服务器执行健康检查
   * 若当前服务器不可用且存在健康的服务器，则自动切换
   * @returns {Promise<Array<{url: string, ok: boolean}>>}
   */
  async checkAll() {
    const results = await Promise.all(
      this.servers.map(async url => {
        const ok = await this.ping(url)
        this.statusMap[url] = ok ? 'healthy' : 'unhealthy'
        return { url, ok }
      })
    )

    // 当前服务器挂了且有健康的服务器，自动切换
    if (this.statusMap[this.currentUrl] === 'unhealthy') {
      const healthy = this.servers.find(u => this.statusMap[u] === 'healthy')
      if (healthy) {
        this.currentUrl = healthy
        window.baseURL = healthy
        EventBus.$emit('server-changed', healthy)
      }
    }

    return results
  }

  /**
   * 启动定时健康检查
   */
  startChecking() {
    this.stopChecking()
    // 立即执行一次检查
    this.checkAll()
    this.timer = setInterval(() => this.checkAll(), HEALTH_CHECK_INTERVAL)
  }

  /**
   * 停止定时健康检查
   */
  stopChecking() {
    if (this.timer) {
      clearInterval(this.timer)
      this.timer = null
    }
  }

  /**
   * 销毁实例（组件卸载时调用）
   */
  destroy() {
    this.stopChecking()
  }
}

export const apiHealth = new ApiHealthManager()